import { URL_PARAMS } from "@/constants/urlParams";

/**
 * Date bounds and vacancy-dimension filters shared by pages reporting over reporting data. Extracted
 * from `GeneralReportingConfig` so a second consumer doesn't import another page's internals.
 */

const today = new Date();
const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
const oneWeekAgo = new Date(yesterday.getTime() - 6 * 24 * 60 * 60 * 1000);

/** Minimum selectable date for reporting (inclusive). */
export const REPORTING_MIN_DATE = new Date(2026, 0, 1);

/** Default start date when no URL param (one week ago). */
export const DEFAULT_START_DATE = oneWeekAgo;

/** Default end date when no URL param (yesterday). */
export const DEFAULT_END_DATE = yesterday;

export const REPORTING_FILTER_KEYS = [
  "department",
  "city",
  "vacancyId",
  "search",
  "field",
  "company",
  "country",
] as const;

export const FILTER_KEYS = [...REPORTING_FILTER_KEYS, "integrations", "campaignIds"] as const;

/** Filter keys used for chips and drawer; excludes search (title filter) so it has no chip and is not cleared on reset. */
export const FILTER_KEYS_FOR_CHIPS = FILTER_KEYS.filter((k) => k !== "search");

/** URL param keys used to build vacancyFilters (REPORTING_FILTER_KEYS minus "search"). */
export const VACANCY_FILTER_URL_KEYS = [
  "vacancyId",
  "department",
  "company",
  "country",
  "city",
  "field",
] as const;

/** Translation key for each filter key when shown as active filter label. Default is `common.${key}`. */
export const FILTER_LABEL_KEYS: Partial<Record<(typeof FILTER_KEYS)[number], string>> = {
  search: "reporting.titleColumn",
  vacancyId: "reporting.vacancyId",
  integrations: "reporting.integrationName",
  campaignIds: "reporting.campaignName",
};

/** Keys for filter sections that get options from the API (reportingFilterOptions). */
export const FILTER_OPTION_KEYS = [
  "vacancyId",
  "department",
  "company",
  "country",
  "city",
  "field",
] as const;

export type FilterOptionKey = (typeof FILTER_OPTION_KEYS)[number];

/**
 * Filter section config: display order and type. Page adds sourceOptions (integration names),
 * campaigns (from /api/v2/Campaigns), or options (from filter counts) at runtime.
 */
export const FILTER_SECTIONS_CONFIG = [
  { key: "campaignIds", titleKey: "reporting.campaignName", type: "campaigns" as const },
  { key: "integrations", titleKey: "reporting.integrationName", type: "sources" as const },
  { key: "vacancyId", titleKey: "reporting.vacancyId", type: "options" as const },
  { key: "department", titleKey: "common.department", type: "options" as const },
  { key: "company", titleKey: "common.company", type: "options" as const },
  { key: "country", titleKey: "common.country", type: "options" as const },
  { key: "city", titleKey: "common.city", type: "options" as const },
  { key: "field", titleKey: "common.field", type: "options" as const },
] as const;

/** URL param keys to preserve when applying filters in the drawer. */
export const FILTER_PRESERVE_PARAM_KEYS = [
  URL_PARAMS.ORDER_BY,
  URL_PARAMS.DESCENDING,
  "startDate",
  "endDate",
] as const;
